/**
 * Product-aware suggestion chips for the admin portal's splash hero (AB#27211).
 *
 * A port of the platform hub's `homeSuggestions` (syrenis-frontends/apps/syrenis-platform-hub/
 * src/components/help/homeSuggestions.ts) so the two heroes offer the same questions for the same
 * products. Keep the pools in step.
 *
 * Static by design: no backend exists yet for suggestions derived from tenant data or recent
 * activity, so these are hand-written per product and chosen from the products the user can
 * actually reach. Labels are kept short so three fit on one row. The first slot is always the core
 * "what first" question, because that is where new users start; the rest round-robin one question
 * per visible product, in the order the products appear, until the limit is reached.
 */
export const HERO_SUGGESTIONS_BY_PRODUCT = {
	'consent-banners': ['Which banners have no GPC profile?', 'How do geo configurations pick a banner?'],
	'consent-configuration': ['Channels vs statements?', 'How do brands partition configuration?'],
	'privacy-notice-management': ['How do I add a notice language?'],
	'site-scanner': ['What did my last scan find?'],
	'risk-portal': ['How do I record a new risk?'],
	'mobile-consent': ['How do I set up the mobile SDK?'],
	'preference-page-analytics': ['How are consent rates calculated?'],
	'administration-portal': ['How do I add a user?']
}

export const HERO_SUGGESTION_FIRST = 'What should I set up first?'

/**
 * Product-agnostic top-up, used when the products a user can reach cannot fill the row. Mirrors the
 * hub's `HELP_ASSISTANT_HOME_QUESTIONS`. Deliberately not one product's own pool: a fallback drawn
 * from, say, consent configuration would suggest a feature the viewer may have no access to.
 */
export const HERO_SUGGESTIONS_CORE = [
	'What should I configure first as a new customer?',
	'What is the difference between a channel and a statement?',
	'How do brands partition my configuration?'
]

export const HERO_SUGGESTION_LIMIT = 3

/**
 * Picks up to `limit` questions for the products the user can see.
 *
 * @param {readonly string[]} productIds product identifiers, in the order they appear on the page
 * @param {number} [limit]
 * @returns {string[]}
 */
export function heroSuggestions (productIds = [], limit = HERO_SUGGESTION_LIMIT) {
	const picked = []
	const add = question => {
		if (picked.length < limit && !picked.includes(question)) picked.push(question)
	}

	add(HERO_SUGGESTION_FIRST)

	const pools = productIds.map(id => [...(HERO_SUGGESTIONS_BY_PRODUCT[id] ?? [])])
	let progressed = true
	while (picked.length < limit && progressed) {
		progressed = false
		for (const pool of pools) {
			const question = pool.shift()
			if (!question) continue
			progressed = true
			add(question)
			if (picked.length >= limit) break
		}
	}

	// Top up from the product-agnostic list if the visible products did not fill the row.
	if (picked.length < limit) {
		for (const question of HERO_SUGGESTIONS_CORE) {
			add(question)
			if (picked.length >= limit) break
		}
	}

	return picked
}
